import { useCallback, useEffect, useRef } from 'react';
import { rebalanceCalloutColumnsInTree } from '../../../utils/calloutColumns';
import { snapshotEditableCaret } from '../../../utils/caretUtils';
import { focusEditable } from '../../../utils/focusUtils';

const packCalloutColumns = (tree) => {
  const next = rebalanceCalloutColumnsInTree(tree);
  if (next === tree) return tree;
  const caret = snapshotEditableCaret();
  if (caret) {
    focusEditable(caret.id, {
      offset: caret.offset,
      preventScroll: true,
    });
  }
  return next;
};

/**
 * Content-only edits update `blocksRef` synchronously but propagate to React
 * state on a trailing debounce. Structural commits flush instantly.
 */
export default function useCoalescedCommit({
  blocks,
  blocksRef,
  onChangeRef,
  pushHistory,
}) {
  const pendingTimerRef = useRef(null);
  const idleHandleRef = useRef(null);
  const dirtyRef = useRef(false); // a coalesced commit hasn't reached the host yet
  // Tree we just flushed structurally — ignore stale `blocks` props until the
  // host echoes this same reference (rename bumps variablesHistoryEpoch and can
  // re-render DocEditor before the parent applies setBlocks).
  const pendingEchoRef = useRef(null);

  // Adopt the host tree when props update — but never clobber blocksRef
  // while a coalesced commit is still ahead of the host (and never on
  // unrelated re-renders like drag activeId flips, which used to reset
  // the ref to a stale props snapshot mid-flight).
  useEffect(() => {
    if (dirtyRef.current) return;

    if (pendingEchoRef.current) {
      if (blocks === pendingEchoRef.current) {
        pendingEchoRef.current = null;
        blocksRef.current = blocks;
      }
      // Stale props (pre-echo) — keep the committed tree in blocksRef
      return;
    }

    blocksRef.current = blocks;
  }, [blocks, blocksRef]);

  const commit = useCallback(
    (tree, { record = true, coalesce = false } = {}) => {
      // Same tree reference: nothing to persist. Tick-only undo/redo
      // restores overrides without rewriting the block tree.
      if (tree === blocksRef.current) return;
      if (record) {
        pushHistory(coalesce);
      }
      // Structural edits rebalance immediately. Typing waits for the flush
      // below so a growing paragraph doesn't hop columns mid-keystroke.
      const next = coalesce ? tree : packCalloutColumns(tree);
      blocksRef.current = next;

      const cancelIdle = () => {
        if (idleHandleRef.current != null && typeof cancelIdleCallback === 'function') {
          cancelIdleCallback(idleHandleRef.current);
        }
        idleHandleRef.current = null;
      };
      const doFlush = () => {
        idleHandleRef.current = null;
        if (pendingTimerRef.current) return; // superseded by a newer coalesced commit
        if (!dirtyRef.current) return;
        dirtyRef.current = false;
        const balanced = packCalloutColumns(blocksRef.current);
        blocksRef.current = balanced;
        pendingEchoRef.current = balanced;
        onChangeRef.current(balanced);
      };

      if (coalesce) {
        dirtyRef.current = true;
        pendingEchoRef.current = null;
        if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
        cancelIdle();
        // Longer than a normal inter-keystroke gap (~150-300ms), so continuous
        // typing genuinely coalesces into one flush at the pause. The flush
        // itself (host re-render of the whole tree) then waits for an idle
        // slot — otherwise it fires exactly when the user resumes typing
        // after a short pause, which reads as a per-action hitch (worst on
        // battery-throttled CPUs).
        pendingTimerRef.current = setTimeout(() => {
          pendingTimerRef.current = null;
          if (typeof requestIdleCallback === 'function') {
            idleHandleRef.current = requestIdleCallback(doFlush, { timeout: 1000 });
          } else {
            doFlush();
          }
        }, 500);
        return;
      }
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
      cancelIdle();
      dirtyRef.current = false;
      pendingEchoRef.current = next;
      onChangeRef.current(next);
    },
    [blocksRef, onChangeRef, pushHistory]
  );

  // Unsent keystrokes must still reach the host when the editor unmounts
  // (closing the doc within the debounce window) — child cleanup runs before
  // the host's own unmount flush, so the save path still sees them.
  useEffect(
    () => () => {
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
      if (idleHandleRef.current != null && typeof cancelIdleCallback === 'function') {
        cancelIdleCallback(idleHandleRef.current);
        idleHandleRef.current = null;
      }
      if (dirtyRef.current) {
        dirtyRef.current = false;
        const balanced = packCalloutColumns(blocksRef.current);
        blocksRef.current = balanced;
        onChangeRef.current(balanced);
      }
    },
    [blocksRef, onChangeRef]
  );

  return { commit };
}
